import type { Aabb, DecorationAnchor, InteriorTemplate, WorldManifest } from "@townsy/contracts";
import { getCatalogItem, type CatalogItemDef } from "./catalog";
import { aabbAt } from "./geometry";

/** One placed item as it appears in PropertyPublic.home.placedItems. */
export interface PlacementRef {
  anchorId: string;
  itemId: string;
  rotation: number;
}

/** True when the anchor accepts the item and the rotation is one the anchor allows (within 1e-3 rad). */
export function anchorAccepts(anchor: DecorationAnchor, item: CatalogItemDef, rotation: number): boolean {
  if (!item.anchorTypes.includes(anchor.type)) return false;
  return anchor.allowedRotations.some((r) => Math.abs(normalize(r) - normalize(rotation)) < 1e-3);
}

function normalize(a: number): number {
  const t = Math.PI * 2;
  return ((a % t) + t) % t;
}

/** Quarter turns (0..3) of a rotation, snapped to the nearest. */
function quarterTurns(rotation: number): number {
  return Math.round(normalize(rotation) / (Math.PI / 2)) % 4;
}

/**
 * A rectangle given in the item's local frame (x0..x1 along local x, z0..z1 along local +z = the
 * item's front) turned by a quarter-turn yaw and placed at (ox, oz). Uses the renderer's rotation
 * (Babylon RotationY): world = (ox + x cos + z sin, oz - x sin + z cos).
 */
function localRect(ox: number, oz: number, rotation: number, x0: number, x1: number, z0: number, z1: number): Aabb {
  const q = quarterTurns(rotation);
  const c = [1, 0, -1, 0][q]!;
  const s = [0, 1, 0, -1][q]!;
  const xs: number[] = [];
  const zs: number[] = [];
  for (const [x, z] of [
    [x0, z0],
    [x1, z0],
    [x1, z1],
    [x0, z1],
  ] as const) {
    xs.push(ox + x * c + z * s);
    zs.push(oz - x * s + z * c);
  }
  return { minX: Math.min(...xs), minZ: Math.min(...zs), maxX: Math.max(...xs), maxZ: Math.max(...zs) };
}

/**
 * Collider boxes for one placed item (empty = walkable). Box items use their footprint (rotated by
 * quarter turns, optionally offset toward their front); fences are two boxes leaving the gate gap
 * on the front path open (ADR-0006).
 */
export function decorationColliders(anchor: DecorationAnchor, item: CatalogItemDef, rotation: number): Aabb[] {
  const { x, z } = anchor.position;
  if (item.fence) {
    const f = item.fence;
    const inner = f.gateHalfWidth - f.postHalf;
    const outer = f.halfSpan + f.postHalf;
    return [localRect(x, z, rotation, -outer, -inner, -f.halfDepth, f.halfDepth), localRect(x, z, rotation, inner, outer, -f.halfDepth, f.halfDepth)];
  }
  if (!item.footprint) return [];
  const { width, depth } = item.footprint;
  const off = item.footprintOffset ?? 0;
  if (off === 0) {
    const quarter = quarterTurns(rotation) % 2 === 1;
    return [aabbAt(x, z, quarter ? depth : width, quarter ? width : depth)];
  }
  return [localRect(x, z, rotation, -width / 2, width / 2, off - depth / 2, off + depth / 2)];
}

/**
 * The union box of an item's colliders, or null when it is walkable. Kept for callers that predate
 * fence colliders: for a fence this is the whole run INCLUDING the gate; use decorationColliders for
 * collision.
 */
export function decorationCollider(anchor: DecorationAnchor, item: CatalogItemDef, rotation: number): Aabb | null {
  const boxes = decorationColliders(anchor, item, rotation);
  if (!boxes.length) return null;
  return {
    minX: Math.min(...boxes.map((b) => b.minX)),
    minZ: Math.min(...boxes.map((b) => b.minZ)),
    maxX: Math.max(...boxes.map((b) => b.maxX)),
    maxZ: Math.max(...boxes.map((b) => b.maxZ)),
  };
}

/**
 * Yard item colliders for the street. The realtime server and the client's prediction MUST call this
 * with the same placements (from PropertyPublic.home.placedItems with scope "yard") or prediction drifts.
 */
export function yardDecorationColliders(
  manifest: WorldManifest,
  placements: ReadonlyArray<PlacementRef & { propertyId: string }>,
): Aabb[] {
  const out: Aabb[] = [];
  for (const p of placements) {
    const plot = manifest.plots.find((x) => x.id === p.propertyId);
    const anchor = plot?.decorationAnchors.find((a) => a.id === p.anchorId);
    const item = getCatalogItem(p.itemId);
    if (!anchor || !item) continue;
    out.push(...decorationColliders(anchor, item, p.rotation));
  }
  return out;
}

/** Furniture colliders inside a home (interior placements of that home). */
export function interiorDecorationColliders(template: InteriorTemplate, placements: ReadonlyArray<PlacementRef>): Aabb[] {
  const out: Aabb[] = [];
  for (const p of placements) {
    const anchor = template.decorationAnchors?.find((a) => a.id === p.anchorId);
    const item = getCatalogItem(p.itemId);
    if (!anchor || !item) continue;
    out.push(...decorationColliders(anchor, item, p.rotation));
  }
  return out;
}

/** The yard anchor that holds a front fence. */
export const FENCE_ANCHOR_ID = "fence_front";

/**
 * Plots with something placed on their front fence anchor (yard placements). The renderer replaces
 * the front hedges with the fence on these plots (residential.ts plotGeometryKey), so streetDecor /
 * streetColliders drop the hedges for them.
 */
export function fencedPlotIds(placements: ReadonlyArray<PlacementRef & { propertyId: string }>): Set<string> {
  const out = new Set<string>();
  for (const p of placements) {
    if (p.anchorId === FENCE_ANCHOR_ID) out.add(p.propertyId);
  }
  return out;
}
